import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { PlusCircleIcon } from '@heroicons/react/24/outline';
import TGButton from 'src/components/ui/TGButton';
import { DrawerComponent } from 'src/components/DialogComponent';
import { FlexCol } from 'src/utils/flexLayouts';
import { CreateDoingForm } from '../formAddAction/FormAddActions';
import FilterForPlanAction from './filterForPlanAction/FilterForPlanAction';
import ContentPlanActions from './contentPlanAction/ContentPlanActions';
import { debounce, isEqual } from 'lodash';
import { ActionDataType, IFilters, PlanActionProps } from '../../types/typesVPO';
import ExportToExcelVPO from '../exportExcel/ExportToExcelPLanAction';
import { useAppDispatch, useAppSelector } from 'src/hooks/redux';
import { TooltipItem } from 'src/utils/Tooltip';
import { FILTER_CONFIG, getInitialFilters, INITIAL_FILTERS_OPEN } from './filterForPlanAction/filterData';
import { selectModelData } from '../../store/filtersData/selector';
import { fetchData } from '../../store/filtersData/actions';
import { transformDataForFilters, transformDataForFiltersRequirements } from 'src/pages/DashboardPage/vpo/utils/plan_actions/plan_actions';

function PlanAction({ filterDefault, context }: PlanActionProps) {
  const dispatch = useAppDispatch();
  // console.log('propsData', filterDefault, groupDefault, idColumn);
  // console.log('context',context)
  const [open, setOpen] = useState(false);
  const [pauseContentLoading, setPauseContentLoading] = useState(false);
  const [actionsData, setActionsData] = useState<ActionDataType[]>([]);
  const [currentFilters, setCurrentFilters] = useState<IFilters>(getInitialFilters(context));

  const FilterColumns = useAppSelector((state: any) => selectModelData(state, 'vpo-columns'));
  const FilteGroup = useAppSelector((state: any) => selectModelData(state, 'vpo-groups').filter((group) => group.title !== 'SOP'));
  const Filteactories = useAppSelector((state: any) => selectModelData(state, 'vpo-factories'));
  const FilterColumnBlock = useAppSelector((state: any) => selectModelData(state, 'vpo-column-blocks'));
  const FilterRequirements = useAppSelector((state: any) => selectModelData(state, 'vpo-requirements'));
  const FilterStatuses = useAppSelector((state: any) => selectModelData(state, 'mt-actions-statuses').filter((status) => status.IsStatus !== false));
  const FilterDepartments = useAppSelector((state: any) => selectModelData(state, 'vpo-departments'));
  const allMeetings = useAppSelector((state: any) => selectModelData(state, 'mt-meetings'));
  const MTActions = useAppSelector((state: any) => selectModelData(state, 'mt-actions'));
// console.log('FilterRequirements',FilterRequirements)
  //! Совещания => связаны с действиями
  const FilterMeetings = useMemo(() => {
    if (!MTActions || !allMeetings) return [];
    const meetingIds = MTActions.map((action: any) => action.Meeting?.id || action.meeting?.id).filter(Boolean);
    return allMeetings.filter((meeting: any) => meetingIds.includes(meeting.id));
  }, [allMeetings, MTActions]);

  //! Процессы на основе выбранных требований
  const FilterProcesses = useMemo(() => {
    return FilterRequirements.filter((req: any) => currentFilters?.demand?.selectedIds.includes(req.id))
      .map((req: any) => req.process)
      .filter((process: any, index: number, self: any[]) => index === self.findIndex((p) => p.id === process.id));
  }, [FilterRequirements, currentFilters?.demand?.selectedIds]);

  //! Получаю группы, совещания, действия и статусы
  useEffect(() => {
    dispatch(
      fetchData(
        'vpo-groups',
        {},
        {
          page: 1,
          pageSize: 100,
        }
      )
    );
    dispatch(
      fetchData(
        'mt-meetings',
        {},
        {
          page: 1,
          pageSize: 100,
        }
      )
    );
    dispatch(
      fetchData(
        'mt-actions',
        {},
        {
          page: 1,
          pageSize: 1000,
        }
      )
    );
     dispatch(
      fetchData(
        'mt-actions-statuses',
        {},
        {
          page: 1,
          pageSize: 1000,
        }
      )
    );
  }, [dispatch]);

  //! При изменении группы получаю колонны и заводы
  useEffect(() => {
    if (currentFilters?.groupPlan?.selectedIds) {
      dispatch(fetchData('vpo-columns', { group: currentFilters?.groupPlan?.selectedIds }, { page: 1, pageSize: 100 }));
      dispatch(fetchData('vpo-factories', { group: currentFilters?.groupPlan?.selectedIds }, { page: 1, pageSize: 100 }));
    }
  }, [currentFilters?.groupPlan?.selectedIds, dispatch]);

  //! При изменении колонны получаю блок колонны
  useEffect(() => {
    if (currentFilters?.columnPlanAction?.selectedIds) {
      dispatch(
        fetchData(
          'vpo-column-blocks',
          {
            column: currentFilters?.columnPlanAction?.selectedIds,
          },
          {
            page: 1,
            pageSize: 100,
          }
        )
      );
    }
  }, [currentFilters?.columnPlanAction?.selectedIds, dispatch]);

  //! При изменении блока колонны получаю требования
  useEffect(() => {
    if (currentFilters?.columnBlock?.selectedIds && currentFilters?.level?.selected) {
      dispatch(
        fetchData(
          'vpo-requirements',
          {
            block: currentFilters?.columnBlock?.selectedIds,
            maturityLevel: currentFilters?.level?.selected,
          },
          {
            page: 1,
            pageSize: 100,
          }
        )
      );
    }
  }, [currentFilters?.columnBlock?.selectedIds, currentFilters?.level?.selected, dispatch]);

  //! При изменении завода и колонны получаю подразделение
  useEffect(() => {
    if (currentFilters?.factory?.selectedIds && currentFilters?.columnPlanAction?.selectedIds) {
      dispatch(
        fetchData(
          'vpo-departments',
          {
            factories: currentFilters?.factory?.selectedIds,
            columns: currentFilters?.columnPlanAction?.selectedIds,
          },
          {
            page: 1,
            pageSize: 100,
          }
        )
      );
    }
  }, [currentFilters?.factory?.selectedIds, currentFilters?.columnPlanAction?.selectedIds, dispatch]);

  const transformedColumns = React.useMemo(() => transformDataForFilters(FilterColumns), [FilterColumns]);
  const transformedroups = React.useMemo(() => transformDataForFilters(FilteGroup), [FilteGroup]);
  const transformedFactories = React.useMemo(() => transformDataForFilters(Filteactories), [Filteactories]);
  const transformedColumnBlock = React.useMemo(() => transformDataForFilters(FilterColumnBlock), [FilterColumnBlock]);
  const transformedRequirements = React.useMemo(() => transformDataForFiltersRequirements(FilterRequirements), [FilterRequirements]);
  const transformedProcesses = React.useMemo(() => transformDataForFilters(FilterProcesses), [FilterProcesses]);
  const transformedDepartments = React.useMemo(() => transformDataForFilters(FilterDepartments), [FilterDepartments]);
  const transformedMeetings = React.useMemo(() => transformDataForFilters(FilterMeetings), [FilterMeetings]);
  const transformedStatuses = React.useMemo(() => transformDataForFilters(FilterStatuses), [FilterStatuses]);
// console.log('transformedRequirements',transformedRequirements)
  const debouncedSetFilters = useCallback(
    debounce((newFilters: IFilters) => {
      // console.log('фильтра', newFilters )
      setCurrentFilters(newFilters);
    }, 1000),
    []
  );

  const handleFiltersChange = useCallback(
    (newFilters: IFilters) => {
      // console.log('Новые фильтры:', newFilters);
      debouncedSetFilters(newFilters);
    },
    [debouncedSetFilters]
  );

  const closeDialog = useCallback(() => {
    setOpen(false);
    setPauseContentLoading(false);
  }, []);

  const toggleDoing = useCallback(
    (value: boolean) => {
      setOpen(value);
      setPauseContentLoading(value);
    },
    [dispatch]
  );

  const handleActionsData = useCallback((data: ActionDataType[]) => {
    setActionsData(data);
  }, []);

  const filterConfigs = useMemo(
    () => ({
      meeting: { items: transformedMeetings },
      status: { items: transformedStatuses },
      groupPlan: { items: transformedroups },
      columnPlanAction: { items: transformedColumns },
      columnBlock: { items: transformedColumnBlock },
      subdivision: { items: transformedDepartments },
      process: { items: transformedProcesses },
      demand: { items: transformedRequirements },
      factory: { items: transformedFactories },
    }),
    [transformedMeetings, transformedStatuses, transformedColumns, transformedroups, transformedColumnBlock, transformedDepartments, transformedProcesses, transformedRequirements, transformedFactories]
  );

  const filtersMap = useMemo(
    () => ({
      meeting: transformedMeetings,
      status: transformedStatuses,
      groupPlan: transformedroups,
      columnPlanAction: transformedColumns,
      columnBlock: transformedColumnBlock,
      subdivision: transformedDepartments,
      process: transformedProcesses,
      demand: transformedRequirements,
      factory: transformedFactories,
    }),
    [transformedMeetings, transformedStatuses, transformedColumns, transformedroups, transformedColumnBlock, transformedDepartments, transformedProcesses, transformedRequirements, transformedFactories]
  );

  return (
    <div className='flex justify-between mt-2'>
      <div className='w-[320px] mr-2'>
        <FilterForPlanAction
          onFiltersChange={handleFiltersChange}
          filterConfigs={filterConfigs}
          filtersMap={filtersMap}
          initialFilters={getInitialFilters(context)}
          filtersOpen={INITIAL_FILTERS_OPEN}
          filtersConfig={FILTER_CONFIG}
          responsibleFil={true}
          heightFilter={'h-[calc(100vh-160px)]'}
        />
      </div>
      <div className='flex-1 flex flex-col min-w-0'>
        <div className='flex mb-2'>
          <div className='flex-1 mr-2'>
            <TGButton icon={<PlusCircleIcon width={20} height={20} />} onClick={() => toggleDoing(true)} color='white' className='w-full'>
              Добавить действие
            </TGButton>
            <DrawerComponent open={open} zIndex={50} percentage='87' toggleDrawer={closeDialog}>
              <FlexCol className='p-10'>
                <h1 className='mb-8 text-black'>Действия</h1>
                <CreateDoingForm
                  toggleDoing={toggleDoing}
                  predefinedValues={{
                    factoryID: filterDefault?.id,
                  }}
                  resetSelect={true}
                  context={context}
                  loader={true}
                />
              </FlexCol>
            </DrawerComponent>
          </div>
          <TooltipItem position='top-left' tooltipsText={'Экспорт в Excel'}>
            <ExportToExcelVPO data={actionsData} />
          </TooltipItem>
        </div>
        <div className='flex-1 min-w-0'>
          <ContentPlanActions filters={currentFilters} onDataUpdate={handleActionsData} pauseLoading={pauseContentLoading} />
        </div>
      </div>
    </div>
  );
}

export default React.memo(PlanAction);
